import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import CompanySearch from '../components/CompanySearch';
import { logger } from '../utils/logger';
import activityTracker from '../user/ActivityTracker';
// 財務GPT分析組件
const FinanceGPT = () => {
  const [symbol, setSymbol] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisResult, setAnalysisResult] = useState('');
  const [isAnalysisComplete, setIsAnalysisComplete] = useState(false);
  
  const ws = useRef(null);
  const resultRef = useRef(null);

  // 處理WebSocket連接關閉
  const handleClose = () => {
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  };

  // 組件卸載時關閉WebSocket連接
  useEffect(() => {
    activityTracker.trackPageView('finance_gpt');
    return () => {
      handleClose();
    };
  }, []);

  // 處理公司選擇
  const handleCompanySelect = (selectedCompany) => {
    // 獲取公司代碼
    const selectedSymbol = selectedCompany?.symbol || selectedCompany;
    
    if (selectedSymbol) {
      setSymbol(selectedSymbol);
    }
  };

  // 處理分析請求
  const handleAnalyze = () => {
    if (!symbol) {
      setError('請輸入股票代碼');
      return;
    }

    setError(null);
    setAnalysisResult('');
    setIsAnalysisComplete(false);
    setIsLoading(true);

    // 關閉之前的WebSocket連接
    handleClose();

    // 創建新的WebSocket連接
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.hostname === 'localhost'
      ? 'localhost:5080'
      : window.location.host;
    const wsUrl = `${wsProtocol}://${wsHost}/ws-app`;
    ws.current = new WebSocket(wsUrl);

    // WebSocket打開時發送請求
    ws.current.onopen = () => {
      ws.current.send(JSON.stringify({
        type: 'financial-analysis',
        symbol: symbol
      }));
    };

    // 處理WebSocket消息
    ws.current.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        
        if (response.error) {
          setError(response.error);
          setIsLoading(false);
          return;
        }

        if (response.type === 'financial' && response.content) {
          setAnalysisResult(prev => prev + response.content);
          
          // 自動滾動到底部
          if (resultRef.current) {
            resultRef.current.scrollTop = resultRef.current.scrollHeight;
          }
        }

        if (response.type === 'complete') {
          setIsLoading(false);
          setIsAnalysisComplete(true);
        }
      } catch (err) {
        logger.error('解析WebSocket消息出錯:', err);
      }
    };

    // 處理WebSocket錯誤
    ws.current.onerror = (error) => {
      logger.error('WebSocket錯誤:', error);
      setError('連接伺服器時出錯，請稍後再試');
      setIsLoading(false);
    };

    // 處理WebSocket連接關閉
    ws.current.onclose = () => {
      if (isLoading) {
        setIsLoading(false);
        if (!isAnalysisComplete && !error) {
          setError('連接已關閉，請刷新頁面重試');
        }
      }
    };
  };

  // 格式化分析結果為段落
  const formatAnalysisResult = (text) => {
    if (!text) return null;
    
    // 將文本分割成段落並添加適當的格式
    return text.split('\n').map((line, index) => {
      // 對標題應用加粗樣式
      if (line.match(/^#+ /)) {
        const level = line.match(/^(#+) /)[1].length;
        const title = line.replace(/^#+ /, '');
        return React.createElement(`h${level}`, { key: index }, title);
      }
      
      // 對列表項應用樣式
      if (line.match(/^\s*[-*] /)) {
        return (
          <li key={index}>{line.replace(/^\s*[-*] /, '')}</li>
        );
      }
      
      // 對普通段落應用樣式
      if (line.trim() === '') {
        return <br key={index} />;
      }
      
      return <p key={index}>{line}</p>;
    });
  };

  return (
    <Container className="mt-4 mb-5">
      <Row>
        <Col>
          <h2 className="mb-4">財務GPT分析 Beta</h2>
          <Card className="mb-4">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>股票代碼</Form.Label>
                <div className="mb-2">
                  <CompanySearch onSelect={handleCompanySelect} />
                </div>
                <Form.Control 
                  type="text" 
                  placeholder="如：AAPL" 
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  disabled={isLoading}
                  className="mt-2"
                />
                <Form.Text className="text-muted">
                  輸入或搜索你想分析的公司股票代碼
                </Form.Text>
              </Form.Group>
              
              <Button 
                variant="primary" 
                onClick={handleAnalyze}
                disabled={isLoading || !symbol}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    分析中...
                  </>
                ) : '開始分析'}
              </Button>
            </Card.Body>
          </Card>

          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          {(analysisResult || isLoading) && (
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">分析結果</h5>
                {isLoading && (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">加載中...</span>
                  </Spinner>
                )}
              </Card.Header>
              <Card.Body 
                className="analysis-result" 
                ref={resultRef}
                style={{
                  maxHeight: '600px',
                  overflowY: 'auto'
                }}
              >
                {formatAnalysisResult(analysisResult)}
                {isLoading && !analysisResult && (
                  <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">分析中...</span>
                    </Spinner>
                    <p className="mt-3">正在分析財務數據，請稍候...</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <style jsx>{`
        h2 {
          font-size: 24px;
        }
        .card-title {
          font-size: 18px;
        }
        .form-label {
          font-size: 16px;
        }
        .form-control {
          font-size: 14px;
        }
        .form-text {
          font-size: 12px;
        }
        .btn {
          font-size: 14px;
        }
        .card-header h5 {
          font-size: 18px;
        }
        .analysis-result {
          font-size: 14px;
        }
        .analysis-result p {
          font-size: 14px;
          margin-bottom: 8px;
        }
        .analysis-result h1 {
          font-size: 20px;
        }
        .analysis-result h2 {
          font-size: 18px;
        }
        .analysis-result h3 {
          font-size: 16px;
        }
        .analysis-result li {
          font-size: 14px;
          margin-bottom: 4px;
        }

        @media screen and (max-width: 768px) {
          h2 {
            font-size: 20px;
          }
          .card-title {
            font-size: 16px;
          }
          .form-label {
            font-size: 14px;
          }
          .form-control {
            font-size: 12px;
          }
          .form-text {
            font-size: 11px;
          }
          .btn {
            font-size: 12px;
          }
          .card-header h5 {
            font-size: 16px;
          }
          .analysis-result {
            font-size: 12px;
          }
          .analysis-result p {
            font-size: 12px;
            margin-bottom: 6px;
          }
          .analysis-result h1 {
            font-size: 18px;
          }
          .analysis-result h2 {
            font-size: 16px;
          }
          .analysis-result h3 {
            font-size: 14px;
          }
          .analysis-result li {
            font-size: 12px;
            margin-bottom: 3px;
          }
        }

        @media screen and (max-width: 480px) {
          h2 {
            font-size: 16px;
          }
          .card-title {
            font-size: 14px;
          }
          .form-label {
            font-size: 12px;
          }
          .form-control {
            font-size: 11px;
          }
          .form-text {
            font-size: 10px;
          }
          .btn {
            font-size: 11px;
          }
          .card-header h5 {
            font-size: 14px;
          }
          .analysis-result {
            font-size: 11px;
          }
          .analysis-result p {
            font-size: 11px;
            margin-bottom: 4px;
          }
          .analysis-result h1 {
            font-size: 16px;
          }
          .analysis-result h2 {
            font-size: 14px;
          }
          .analysis-result h3 {
            font-size: 12px;
          }
          .analysis-result li {
            font-size: 11px;
            margin-bottom: 2px;
          }
          .card-body {
            padding: 12px;
          }
          .card-header {
            padding: 8px 12px;
          }
          .mb-4 {
            margin-bottom: 1rem !important;
          }
          .mt-4 {
            margin-top: 1rem !important;
          }
          .mb-3 {
            margin-bottom: 0.75rem !important;
          }
          .mt-2 {
            margin-top: 0.5rem !important;
          }
          .mt-3 {
            margin-top: 0.75rem !important;
          }
          .my-5 {
            margin-top: 1rem !important;
            margin-bottom: 1rem !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default FinanceGPT; 